import React, { useState, useRef, useEffect } from 'react';
import { Bug, X, Send, Loader2, GripVertical } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiBaseUrl } from '@/config/api';

const API = getApiBaseUrl();

export function BugReportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  // Initialize position to bottom-right with safe margins
  useEffect(() => {
    const initPosition = () => {
      setPosition({
        x: Math.max(10, window.innerWidth - 60),
        y: Math.max(10, window.innerHeight - 60)
      });
    };
    // Small delay to ensure window dimensions are ready
    setTimeout(initPosition, 100);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 40),
        y: Math.min(prev.y, window.innerHeight - 40)
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 40, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const handleSubmit = async () => {
    if (!report.trim()) {
      toast.error('Please describe the bug you encountered');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/api/bug-report`,
        {
          description: report,
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString()
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.data.success) {
        toast.success('Bug report submitted! Thank you for your feedback.');
        setReport('');
        setIsOpen(false);
      } else {
        toast.error('Failed to submit bug report');
      }
    } catch (error) {
      console.error('Bug report error:', error);
      toast.error('Failed to submit bug report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Draggable Bug Report Button */}
      <div
        ref={buttonRef}
        onMouseDown={handleMouseDown}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 9999,
          cursor: isDragging ? 'grabbing' : 'default',
          userSelect: 'none'
        }}
        className="group"
        data-testid="bug-report-bubble"
      >
        {/* Chat bubble container */}
        <div className="relative">
          {/* Drag handle - appears on hover */}
          <div 
            className="drag-handle absolute -top-2 -left-2 w-5 h-5 bg-zinc-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex items-center justify-center"
            title="Drag to move"
          >
            <GripVertical className="w-3 h-3 text-zinc-400" />
          </div>
          
          {/* Main button */}
          <button
            onClick={() => !isDragging && setIsOpen(true)}
            className="relative flex items-center justify-center bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
            style={{ width: '35px', height: '35px' }}
            title="Report a bug"
            data-testid="bug-report-button"
          >
            <Bug className="w-4 h-4 text-white" />
          </button>
          
          {/* Speech bubble tail */}
          <div 
            className="absolute -bottom-1 right-1 bg-gradient-to-br from-red-500 to-orange-500 transform rotate-45" 
            style={{ width: '10px', height: '10px' }}
          />
        </div>
      </div>

      {/* Bug Report Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-400" />
              Report a Bug
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Describe the issue you encountered. Include what you were trying to do and what happened instead.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <Textarea
              value={report}
              onChange={(e) => setReport(e.target.value)}
              placeholder="I was trying to... and then... but instead..."
              className="min-h-[150px] bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 resize-none"
              data-testid="bug-report-textarea"
            />

            <div className="text-xs text-zinc-500">
              <p>The following info will be included automatically:</p>
              <ul className="list-disc list-inside mt-1 text-zinc-600">
                <li>Current page: {window.location.pathname}</li>
                <li>Your user account (if logged in)</li>
                <li>Browser and screen info</li>
              </ul>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setIsOpen(false)}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !report.trim()}
                className="bg-red-500 hover:bg-red-600 gap-2"
                data-testid="bug-report-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
